import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import PromocionDirecta, { faltaPromo, promoParaEnviar, promoVacia } from "../../../../components/acuerdos/promocionDirecta";
import { RequestPromotionDTO } from "../../../../models/EnrollmentHistoryDTO";
import { EnrollmentUpdateRow, MoneyMove, RepartoContext, TuitionSnapshot, UPDATE_TYPE, UpdateDetailsDTO } from "../../../../models/EnrollmentUpdateDTO";
import S from "./strings";
import { TYPE_LABELS, dinero, errorText, fechaCorta, nombreHorario } from "./helpers";
import { BotonWhatsappAlumno } from "../../../../components/whatsapp/whatsappAlumno";

/** Small modal asking for one text (Qué corregir, motivo, comentario). Empty text keeps the button off. */
export const PromptModal: React.FC<{ title: string; label: string; confirm: string; initial?: string; optional?: boolean; busy?: boolean;
	onCancel: () => void; onConfirm: (text: string) => void; children?: React.ReactNode }> = ({ title, label, confirm, initial, optional, busy, onCancel, onConfirm, children }) => {
	const [text, setText] = useState<string>(initial || "");
	return (
		<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={onCancel}>
			<div className="wau-modal" onClick={(e) => e.stopPropagation()}>
				<h3>{title}</h3>
				{children}
				<div className="wau-campo"><span className="wau-lab">{label}</span>
					<textarea className="wau-entrada" rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus /></div>
				<div className="wau-acciones">
					<button type="button" className="wau-btn s" onClick={onCancel} disabled={busy}>{S.cancel}</button>
					<button type="button" className="wau-btn p" disabled={busy || (!optional && text.trim().length < 3)} onClick={() => onConfirm(text.trim())}>{confirm}</button>
				</div>
			</div>
		</div>
	);
};

/** El reparto de dinero que dejó una actualización aplicada (Cambio a otro curso o Retiro), si movió algo. */
export const auditoriaReparto = (r: EnrollmentUpdateRow): number | null => {
	const rep = r.result?.reparto;
	return r.statusId === 3 && rep && Number(rep.auditItemId) > 0 && Number(rep.moved) > 0 ? Number(rep.auditItemId) : null;
};

/** ¿Quien mira es Caja? (el servidor solo deja deshacer un reparto a Caja). */
export const useEsCaja = (campusId: number, enrollmentId: number | null): boolean => {
	const [caja, setCaja] = useState<boolean>(false);
	useEffect(() => {
		if (!enrollmentId) { return; }
		let vigente = true;
		HttpManager.getInstance().enrollmentUpdatesService.options(campusId, enrollmentId).then((o) => vigente && setCaja(!!o.data.isCaja)).catch(() => { /* sin opciones: no se ofrece */ });
		return () => { vigente = false; };
	}, [campusId, enrollmentId]);
	return caja;
};

/** Deshacer Reparto: pide el motivo y devuelve el dinero a sus mensualidades de origen. El motivo real del servidor se muestra tal cual. */
export const DeshacerReparto: React.FC<{ campusId: number; auditItemId: number; studentName: string; onDone: (msg: string) => void; onError: (msg: string) => void; countryId?: number | null }> = ({ campusId, auditItemId, studentName, onDone, onError, countryId }) => {
	const [abierto, setAbierto] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	return (
		<>
			<button type="button" className="wau-btn s mini" onClick={() => setAbierto(true)}>{S.undoReparto}</button>
			{abierto ? (
				<PromptModal title={S.undoReparto} label={S.undoRepartoReason} confirm={S.undoReparto} busy={busy} onCancel={() => setAbierto(false)}
					onConfirm={(nota) => {
						setBusy(true);
						HttpManager.getInstance().enrollmentUpdatesService.undoReparto(campusId, auditItemId, nota)
							.then((r) => { setAbierto(false); onDone(S.undoRepartoDone(dinero(r.data.amount, countryId))); })
							.catch((e) => { setAbierto(false); onError(errorText(e)); })
							.finally(() => setBusy(false));
					}}>
					<p className="wau-sec" style={{ marginTop: 0 }}>{studentName}</p>
				</PromptModal>
			) : null}
		</>
	);
};

/** Payment plan as month chips: paid, overdue, No cursada, frozen. Only monthly fees (typeId 1). */
export const PlanMeses: React.FC<{ tuitions: TuitionSnapshot[] }> = ({ tuitions }) => (
	<div className="wau-meses wau-num">
		{tuitions.filter((t) => t.typeId === 1).map((t) => {
			const cls = t.progressStatusId === 3 ? "nocursada" : t.statusId === 2 ? "pagada" : t.statusId === 3 ? "vencida" : t.frozen ? "congelada" : "";
			return <span key={"m_" + t.id} className={"wau-mes " + cls} title={t.title}><b>{t.tuitionNumber}</b>{fechaCorta(t.dueDate)}</span>;
		})}
	</div>
);

/*
	Lo pagado que no se cursará. `reparto.pool` solo cuenta mensualidades que ya están No cursadas, y antes de aplicar
	un Cambio a otro curso o un Retiro todavía no lo están: se suma lo pagado de las mensualidades por venir.
*/
export const estimatePool = (d: UpdateDetailsDTO): number => {
	const pool = d.reparto?.pool?.total || 0;
	if (pool > 0) { return pool; }
	if (d.reparto?.poolProjected) { return d.reparto.poolProjected.total || 0; }
	if (d.previewNow?.paidNotCoursedAfter) { return d.previewNow.paidNotCoursedAfter; }
	if (d.typeId !== UPDATE_TYPE.COURSE_CHANGE || !d.enrollment) { return 0; }
	const hoy = new Date().toISOString().slice(0, 10);
	return Math.round(d.enrollment.tuitions
		.filter((t) => t.typeId === 1 && t.progressStatusId !== 2 && (t.startDate || t.dueDate || "") > hoy)
		.reduce((acc, t) => acc + (Number(t.paidAmount) || 0), 0) * 100) / 100;
};

interface RepartoLine { key: string; label: string; sub: string; move: Omit<MoneyMove, "amount">; }

/** Editable split of what was paid and won't be coursed. The rest stays as "Pagado no cursado" (never saldo a favor). */
export const RepartoEditor: React.FC<{ reparto: RepartoContext; pool: number; countryId?: number | null; initial?: MoneyMove[] | null;
	onChange: (moves: MoneyMove[], over: boolean) => void }> = ({ reparto, pool, countryId, initial, onChange }) => {
	const lines: RepartoLine[] = useMemo(() => {
		const out: RepartoLine[] = [];
		(reparto.newPlan?.tuitionNumbers || []).forEach((n) => out.push({ key: "n" + n, label: S.newPlanTuition(n - 1), sub: dinero(reparto.newPlan?.monthlyPrice, countryId), move: { target: "tuition", tuitionNumber: n } }));
		(reparto.otherTuitions || []).forEach((t) => out.push({ key: "t" + t.id, label: `${t.title}${t.courseName ? ", " + t.courseName : ""}`, sub: S.pendingAmount(dinero(t.pending, countryId)), move: { target: "tuition", id: t.id } }));
		(reparto.articles || []).forEach((a) => out.push({ key: "a" + a.id, label: a.title, sub: S.pendingAmount(dinero(a.pending, countryId)), move: { target: "article", id: a.id } }));
		return out;
	}, [reparto, countryId]);
	const [values, setValues] = useState<{ [k: string]: string }>(() => {
		const v: { [k: string]: string } = {};
		(initial || []).forEach((m) => {
			const l = lines.find((x) => x.move.target === m.target && (m.tuitionNumber ? x.move.tuitionNumber === m.tuitionNumber : x.move.id === m.id));
			if (l) { v[l.key] = String(m.amount); }
		});
		return v;
	});
	const total = lines.reduce((acc, l) => acc + (Number(values[l.key]) || 0), 0);
	const rest = Math.round((pool - total) * 100) / 100;
	useEffect(() => {
		onChange(lines.filter((l) => Number(values[l.key]) > 0).map((l) => ({ ...l.move, amount: Number(values[l.key]) })), rest < 0);
	}, [values]);
	return (
		<div className="wau-reparto">
			{lines.map((l) => (
				<div className="wau-rep" key={l.key}>
					<span>{l.label}<span className="sub">{l.sub}</span></span>
					<input className="wau-entrada wau-num" inputMode="decimal" aria-label={l.label} value={values[l.key] ?? ""} placeholder="0"
						onChange={(e) => setValues({ ...values, [l.key]: e.target.value.replace(/[^0-9.]/g, "") })} />
				</div>
			))}
			<div className="wau-rep resto"><span>{S.remainder}</span><span className="wau-num">{dinero(Math.max(0, rest), countryId)}</span></div>
			{rest < 0 ? <p className="wau-oro" style={{ fontSize: 13.5, margin: 0 }}>{S.overAssigned}</p> : ""}
		</div>
	);
};

const fromTo = (d: UpdateDetailsDTO): string => {
	const from = nombreHorario(d.fromSchedule?.name || d.enrollment?.scheduleName) || "—";
	const to = d.toCourse ? `${d.toCourse.name}${d.toSchedule ? ", " + nombreHorario(d.toSchedule.name) : ""}` : nombreHorario(d.toSchedule?.name);
	return to ? `${from} → ${to}` : from;
};

/**
 * One update, ready to apply or return: plan, avance, warnings, sesiones de más (3 opciones SIN default),
 * reparto, WhatsApp and the buttons. Shared by "Por aplicar" and "Cambio a otro curso".
 */
export const UpdateDetail: React.FC<{ campusId: number; countryId?: number | null; id: number; onChanged: (msg: string) => void; notifyError: (msg: string) => void }> = ({ campusId, countryId, id, onChanged, notifyError }) => {
	const http = HttpManager.getInstance();
	const [d, setD] = useState<UpdateDetailsDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [extra, setExtra] = useState<number | null>(null);
	const [extraMonths, setExtraMonths] = useState<string>("");
	const [extraPrice, setExtraPrice] = useState<string>("");
	const [lastSession, setLastSession] = useState<string>("");
	const [money, setMoney] = useState<MoneyMove[]>([]);
	const [over, setOver] = useState<boolean>(false);
	const [prompt, setPrompt] = useState<"return" | "discard" | "resubmit" | null>(null);
	const [promo, setPromo] = useState<RequestPromotionDTO>(promoVacia());

	useEffect(() => {
		let vigente = true;
		setD(null); setExtra(null); setMoney([]); setOver(false); setPrompt(null);
		http.enrollmentUpdatesService.details(campusId, id).then((r) => {
			if (!vigente) { return; }
			setD(r.data);
			setPromo(r.data.options?.promotion?.items ? r.data.options.promotion : promoVacia());
			setExtra(r.data.extraSessionsOption ?? null);
			setExtraMonths(String(r.data.extraMonths ?? r.data.previewNow?.extraMonthsSuggested ?? ""));
			setExtraPrice(r.data.extraPrice != null ? String(r.data.extraPrice) : "");
			setLastSession(r.data.lastSessionTaken != null ? String(r.data.lastSessionTaken) : "");
		}).catch((e) => vigente && notifyError(errorText(e)));
		return () => { vigente = false; };
	}, [campusId, id]);

	if (!d) { return <div className="wau-caja wau-sec">{S.loading}</div>; }

	const p = d.previewNow || d.preview;
	const open = d.statusId === 1 || d.statusId === 2;
	const more = p?.sessionsMore || 0;
	const typeLabel = TYPE_LABELS[d.typeId || 0] || d.typeLabel || S.choose;
	const pool = estimatePool(d);
	const showReparto = open && !!d.reparto && (d.typeId === UPDATE_TYPE.COURSE_CHANGE || pool > 0);
	const isRetiro = d.typeId === UPDATE_TYPE.RETIRED;
	const needsExtra = open && more > 0;
	const esCurso = d.typeId === UPDATE_TYPE.COURSE_CHANGE;
	const faltaPromocion = open && esCurso && (!d.toSchedule || faltaPromo(promo, [d.toSchedule.id]));
	const canApply = open && d.canApply && (!needsExtra || extra !== null) && !over && !faltaPromocion;

	const run = (fn: () => Promise<any>, msg: string) => {
		setBusy(true);
		fn().then(() => { setPrompt(null); onChanged(msg); }).catch((e) => notifyError(errorText(e))).finally(() => setBusy(false));
	};
	const apply = () => run(() => http.enrollmentUpdatesService.apply(campusId, d.id, {
		extraSessionsOption: needsExtra ? extra : null,
		extraMonths: extra === 2 ? Number(extraMonths) || null : null,
		extraPrice: extra === 3 ? Number(extraPrice) || null : null,
		money: showReparto ? money : undefined,
		lastSessionTaken: isRetiro && lastSession ? Number(lastSession) : undefined,
		promotion: esCurso && d.toSchedule ? promoParaEnviar(promo, d.toSchedule.id, {}, true) : undefined
	}), S.applied);

	const sessionsTotal = d.enrollment?.sessionsTotal || 0;
	return (
		<div>
			<div className="wau-caja">
				<div className="wau-fila" style={{ justifyContent: "space-between" }}>
					<div className="wau-campo"><span className="wau-lab">{S.requestedByLine(typeLabel, d.requestedBy, fechaCorta(d.requestedAt))}</span>
						<b style={{ fontSize: 17 }}>{d.studentName}{d.courseName ? ", " + d.courseName : ""}</b></div>
					<span className="wau-estado">{fromTo(d)}</span>
				</div>
				{d.enrollment ? (
					<div className="wau-dos">
						<div className="wau-campo"><span className="wau-lab">{S.plan}{d.enrollment.paidThrough ? ", " + d.enrollment.paidThrough : ""}{d.enrollment.overdue > 0 ? ", " : ""}{d.enrollment.overdue > 0 ? <span className="wau-oro">{S.overdue} {dinero(d.enrollment.overdue, countryId)}</span> : ""}</span>
							<PlanMeses tuitions={d.enrollment.tuitions} /></div>
						<div className="wau-campo"><span className="wau-lab">{S.progress(d.enrollment.sessionsTaken, sessionsTotal)}{d.enrollment.sessionsToRecover > 0 ? ", " : ""}{d.enrollment.sessionsToRecover > 0 ? <span className="wau-oro">{S.lessSessions(d.enrollment.sessionsToRecover)}</span> : ""}</span>
							{p?.target ? <span className="wau-sec" style={{ fontSize: 13.5 }}>{nombreHorario(p.target.scheduleName)}, {S.goesInSession(p.target.classesGiven + 1)}</span> : ""}</div>
					</div>
				) : ""}
				{(p?.warnings || []).length > 0 ? <div style={{ marginTop: 10 }}>{(p?.warnings || []).map((w, i) => <p key={"w" + i} className="wau-adv">{w}</p>)}</div> : ""}
				<div className="wau-hilo">
					<div><span className="cuando">{d.requestedBy}</span><span>"{d.note}"</span></div>
					{d.comments.map((c) => <div key={"c" + c.id}><span className="cuando">{c.author}, {fechaCorta(c.createdAt)}</span><span>{c.text}</span></div>)}
					{d.returnReason ? <div className="wau-oro"><span className="cuando">{S.fixReason}</span>{d.returnReason}</div> : ""}
				</div>
			</div>
			{open && esCurso && d.previousAgreement && !(p?.warnings || []).includes(d.previousAgreement.text) ? <div className="wau-nota" style={{ marginBottom: 12 }}><span>{d.previousAgreement.text}</span></div> : ""}
			{open && esCurso && d.toSchedule ? (
				<div className="wau-caja"><PromocionDirecta campusId={campusId} studentId={d.studentId} studentName={d.studentName} scheduleIds={[d.toSchedule.id]} value={promo} onChange={setPromo} busy={busy} marcar={false} /></div>
			) : ""}
			{needsExtra ? (
				<div className="wau-caja"><h3>{S.extraTitle(more)}</h3>
					<div className="wau-opciones" role="radiogroup">
						{S.extraOptions.map((o) => (
							<label key={"x" + o.id} className={"wau-opcion" + (extra === o.id ? " on" : "")}>
								<input type="radio" name={"extra_" + d.id} checked={extra === o.id} onChange={() => setExtra(o.id)} />
								<span className="t">{o.t}</span><span className="d">{o.d}</span>
							</label>
						))}
					</div>
					{extra === 2 ? <div className="wau-campo" style={{ marginTop: 10, maxWidth: 160 }}><span className="wau-lab">{S.extraMonths}</span>
						<input className="wau-entrada wau-num" inputMode="numeric" value={extraMonths} onChange={(e) => setExtraMonths(e.target.value.replace(/\D/g, ""))} /></div> : ""}
					{extra === 3 ? <div className="wau-campo" style={{ marginTop: 10, maxWidth: 160 }}><span className="wau-lab">{S.extraPrice}</span>
						<input className="wau-entrada wau-num" inputMode="decimal" value={extraPrice} onChange={(e) => setExtraPrice(e.target.value.replace(/[^0-9.]/g, ""))} /></div> : ""}
				</div>
			) : ""}
			{open && isRetiro ? (
				<div className="wau-caja"><div className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.lastSession}</span>
					<input className="wau-entrada wau-num" inputMode="numeric" value={lastSession} onChange={(e) => setLastSession(e.target.value.replace(/\D/g, ""))} /></div></div>
			) : ""}
			{showReparto && d.reparto ? (
				<div className="wau-caja"><h3>{S.reparto}</h3>
					<p className="wau-sec" style={{ fontSize: 13.5 }}>{S.paidNotCoursedLine(dinero(pool, countryId))}</p>
					<RepartoEditor reparto={d.reparto} pool={pool} countryId={countryId} initial={d.money} onChange={(m, o) => { setMoney(m); setOver(o); }} />
				</div>
			) : ""}
			{!open && d.whatsappText ? (
				<div className="wau-wa">{d.whatsappText}<div><BotonWhatsappAlumno campusId={campusId} studentId={d.studentId} nombre={d.studentName} texto sugerido={d.whatsappText} /></div></div>
			) : ""}
			{open ? (
				<div className="wau-acciones">
					{d.statusId === 2 ? <button type="button" className="wau-btn s" disabled={busy} onClick={() => setPrompt("resubmit")}>{S.resubmit}</button> : ""}
					<button type="button" className="wau-btn s" disabled={busy} onClick={() => setPrompt("discard")}>{S.reject}</button>
					{d.canReturn && d.statusId === 1 ? <button type="button" className="wau-btn s" disabled={busy} onClick={() => setPrompt("return")}>{S.askFix}</button> : ""}
					{d.canApply ? <button type="button" className="wau-btn p" disabled={busy || !canApply} onClick={apply}>{S.apply}</button>
						: <span className="wau-sec" style={{ fontSize: 13.5 }}>{S.onlyCaja}</span>}
				</div>
			) : ""}
			{needsExtra && extra === null && d.canApply ? <p className="wau-oro" style={{ fontSize: 13.5, textAlign: "right", marginTop: 6 }}>{S.chooseExtra}</p> : ""}
			{prompt === "return" ? <PromptModal title={S.askFix} label={S.fixReason} confirm={S.askFix} busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.returnUpdate(campusId, d.id, t), S.fixed)} /> : ""}
			{prompt === "discard" ? <PromptModal title={S.reject} label={S.rejectReason} confirm={S.reject} busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.discard(campusId, d.id, t), S.rejected)} /> : ""}
			{prompt === "resubmit" ? <PromptModal title={S.resubmit} label={S.resubmitComment} confirm={S.resubmit} optional busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.resubmit(campusId, d.id, { comment: t || undefined }), S.resubmitted)} /> : ""}
		</div>
	);
};
